import { AppUpdateModal } from '@pwa/components/app-update-modal';
import { CommandPaletteProvider } from '@pwa/lib/command-palette-store';
import { queryClient } from '@pwa/lib/query-client';
import { ThemeProvider } from '@pwa/lib/theme-provider';
import { routeTree } from '@pwa/routeTree.gen';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import '@repo/ui/styles/globals.css';

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: 'intent',
	scrollRestoration: true,
	defaultPreloadStaleTime: 0,
});

const App = () => (
	<ThemeProvider attribute='class' defaultTheme='system' enableSystem disableTransitionOnChange>
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<CommandPaletteProvider>
					<RouterProvider router={router} />
					<Toaster richColors closeButton position='top-center' />
					<AppUpdateModal />
				</CommandPaletteProvider>
			</QueryClientProvider>
		</StrictMode>
	</ThemeProvider>
);

// Smooth in-page scrolling for anchor links and router-driven scroll
// restoration. Set on <html> so CSS `scroll-behavior: smooth` applies
// to all top-level scrollers.
document.documentElement.classList.add('scroll-smooth');

// Mark the document as "loading" so a styled inline placeholder can be
// rendered in `index.html` (matches the visual treatment of <PageLoader />).
// We remove the class as soon as React commits, so styles that gate on
// `[data-loading]` can fall away without a flash.
document.body.dataset.loading = 'true';

const rootElement = document.getElementById('root');

// A more robust mount check: bail out only if the element is missing
// or the app has *already* mounted (root._reactRootContainer exists).
// We deliberately don't gate on `innerHTML` because pre-rendered error
// pages (e.g. 5xx from the worker) leave content behind, and we still
// want React to take over.
if (rootElement && !(rootElement as HTMLElement & { _reactRootContainer?: unknown })._reactRootContainer) {
	const root = createRoot(rootElement);
	root.render(<App />);

	// Clear the loading state on the next frame so the first paint of the
	// real app is already on screen when the CSS swaps off.
	queueMicrotask(() => {
		delete document.body.dataset.loading;
	});
}
